import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import {
  getSliderX,
  isSliderLockedLeft,
  isSliderLockedRight,
  SLIDER_INDEX,
} from '../../shape-helpers/slider-rectangle-helper';
import { SubpointInfo } from '../../subpoint/subpoint.component';
import { getPointString } from '../abstract-shape-helpers';
import { RectangleComponent } from '../rectangle/rectangle.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- svg needs attribute selectors
  selector: '[h3SliderRectangle]',
  templateUrl: './slider-rectangle.component.html',
  styleUrls: [
    '../../shape-parts/outline-host.scss',
    './slider-rectangle.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SliderRectangleComponent
  extends RectangleComponent
  implements OnInit
{
  SLIDER_INDEX = SLIDER_INDEX;

  rectanglePoints$!: Observable<Point2D[]>;
  allSliderPointInfo$!: Observable<SubpointInfo[]>;
  sliderLinePoints$!: Observable<Point2D[] | undefined>;
  sliderLinePointString$!: Observable<string | undefined>;

  override ngOnInit() {
    super.ngOnInit();
    this.rectanglePoints$ = this.points$.pipe(
      map((points) => points.slice(0, 4)),
    );
    this.sliderLinePoints$ = this.points$.pipe(
      map((points) => {
        if (points.length < 5 || isSliderLocked(points)) {
          return undefined;
        }

        const { top, bottom, x } = getSliderCoordinates(points);
        return [
          [x, top],
          [x, bottom],
        ];
      }),
    );
    this.sliderLinePointString$ = this.sliderLinePoints$.pipe(
      map((sliderLinePoints) =>
        sliderLinePoints ? getPointString(sliderLinePoints) : undefined,
      ),
    );
    this.allSliderPointInfo$ = this.buildAllSliderPointInfo$();
  }

  protected override buildAllSubpointInfo$() {
    return super
      .buildAllSubpointInfo$()
      .pipe(map((allBaseSubpointInfo) => allBaseSubpointInfo.slice(0, 4)));
  }

  private buildAllSliderPointInfo$() {
    return super.buildAllSubpointInfo$().pipe(
      map((allBaseSubpointInfo): SubpointInfo[] => {
        const points = allBaseSubpointInfo.map(
          (baseSubpointInfo) => baseSubpointInfo.point,
        );
        if (points.length < 5) {
          return [];
        }

        const sharedInfo = { ...allBaseSubpointInfo[4], cursor: undefined };
        const { top, bottom, x } = getSliderCoordinates(points);
        if (isSliderLocked(points)) {
          return [{ ...sharedInfo, point: [x, (bottom + top) / 2] }];
        } else {
          return [
            { ...sharedInfo, point: [x, top] },
            { ...sharedInfo, point: [x, bottom] },
          ];
        }
      }),
    );
  }
}

function isSliderLocked(points: Point2D[]) {
  return isSliderLockedLeft(points) || isSliderLockedRight(points);
}

function getSliderCoordinates(points: Point2D[]) {
  const top = Math.min(...points.map((p) => p[1]));
  const bottom = Math.max(...points.map((p) => p[1]));
  return { x: getSliderX(points), top, bottom };
}
